import { Injectable, inject } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";

import { SURFACES } from "../../surface/surface.tokens";
import { SurfaceService } from "../../surface/surface.service";
import { LoginService } from "../authentication.service";
import { bounceToLogin } from "./return-url";
import { isTokenExpired, readStoredToken } from "./session-token";

/**
 * Admits an account to `/learn`.
 *
 * Enrolment as a learner is what grants entry, so a trainer who is also enrolled reaches this
 * surface without any role being switched. See {@link AuthGuard} for the `/manage` counterpart.
 */
@Injectable()
export class StudentAuthGuard {
  private readonly router = inject(Router);
  private readonly loginService = inject(LoginService);
  private readonly surfaces = inject(SurfaceService);

  canActivate(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): boolean {
    const token = readStoredToken();
    if (token === null) {
      this.loginService.changeFlag(false);
      bounceToLogin(this.router, state?.url);
      return false;
    }

    if (isTokenExpired(token)) {
      this.loginService.removeLocalStorage();
      bounceToLogin(this.router, state?.url);
      return false;
    }

    // `learnSpineReadyGuard` runs ahead of this one and holds the navigation until the payload
    // lands. When the spine has no answer for this account, `isAvailable` falls back to the
    // legacy role check, so a learner is never refused for an unanswered read.
    if (!this.surfaces.isAvailable("learn")) {
      this.loginService.changeFlag(false);
      void this.router.navigate([this.elsewhere() ?? "/learn/no-access"]);
      return false;
    }

    this.loginService.changeFlag(true);
    return true;
  }

  /**
   * The home of another surface this account holds, or null.
   *
   * Never `/login` from here: the session is valid, and `LoginComponent` sends a stored learner
   * session straight back to `/learn`, so the two bounce forever and paint nothing.
   */
  private elsewhere(): string | null {
    const other = SURFACES.find((s) => s.id !== "learn" && this.surfaces.isAvailable(s.id));
    return other === undefined ? null : other.home;
  }
}
